import { Thumbnail as BaseThumbnail } from '@nucleus/sermon-theme-elements';
import { formatSrcSet } from '@nucleus/sermon-theme-elements/src/lib/media';
import { ImageWeb } from '@nucleus/types/media/image';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { cornerStyleInterpolationFactory } from '../lib/cornerStyle';

interface Props {
  children?: React.ReactNode;
  className?: string;
  image?: ImageWeb;
  isImageVisible?: boolean;
  style?: React.CSSProperties;
  to: string;
}

export const PostExpandedListItem = (props: Props): JSX.Element => {
  const isImageVisible = props.isImageVisible !== false;

  return (
    <Container className={props.className} style={props.style}>
      {isImageVisible && (
        <Media>
          <Link to={props.to}>
            <ThumbnailContainer>
              <Thumbnail
                src={props.image?.src}
                srcSet={formatSrcSet(props.image?.srcSet)}
                blurHash={props.image?.blurHash}
                focalPoint={props.image?.focalPoint}
              />
              <LinearGradient />
            </ThumbnailContainer>
          </Link>
        </Media>
      )}
      <Meta>
        <div>{props.children}</div>
      </Meta>
    </Container>
  );
};

const LinearGradient = styled.div`
  position: absolute;
  inset: 0;

  pointer-events: none;
  background-image: linear-gradient(0deg, #000000, transparent);

  opacity: 0;
  transition: opacity 900ms cubic-bezier(0.23, 1, 0.32, 1);
`;

const Media = styled.div`
  width: 100%;
  @media (min-width: 1200px) {
    max-width: 650px;
    flex: 2 1 650px;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '12px',
    pill: '25px',
  })};

  overflow: hidden;

  :hover {
    ${LinearGradient} {
      opacity: 0.9;
    }
  }
`;

const Thumbnail = styled(BaseThumbnail)`
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '12px',
    pill: '25px',
  })};
`;

const Meta = styled.div`
  flex: 1 2 300px;
  max-width: 85%;
  padding-left: 4.6%;
  padding-bottom: 4.6%;

  @media (min-width: 1200px) {
    max-width: 860px;
    padding-bottom: 0;
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Container = styled.div`
  padding: 25px;
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '18px',
    pill: '40px',
  })};
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  gap: 1.7em;

  @media (min-width: 1200px) {
    flex-direction: row;
    align-items: center;
    gap: 0;
  }

  ${Meta}:first-child {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`;

PostExpandedListItem.Media = Media;
PostExpandedListItem.Meta = Meta;
